import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  EditOutlined,
  PrinterOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PrintableHrmsTransaction } from '@/components/hr/PrintableHrmsTransaction';
import { ENTITY_TYPE_HR_LEAVE_APPLICATION } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import {
  LEAVE_STATUS_COLOR,
  buildLinearWorkflow,
  hrmsStatusColor,
  showApprovalPanel,
} from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';

const { Text } = Typography;

const LeaveApplicationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [extendForm] = Form.useForm();
  const [dutyForm] = Form.useForm();

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.leaveApplication(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getLeaveApplication(id!),
        () => hrmsService.listLeaveApplications(),
        id!
      ),
    enabled: Boolean(id),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveApplication(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveApplications() });
  };

  const workflow = useMemo(
    () =>
      buildLinearWorkflow(
        ['Draft', 'Pending', 'Approved', 'DutyResumed', 'Closed'],
        row?.status === 'PendingApproval' ? 'Pending' : row?.status || 'Draft',
        { rejectedStatuses: ['Rejected'], cancelledStatuses: ['Cancelled'] }
      ),
    [row?.status]
  );

  const run = async (key: string, fn: () => Promise<unknown>, okMsg: string) => {
    setActionLoading(key);
    try {
      const result = await fn();
      message.success(okMsg);
      await invalidate();
      return result;
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || `${okMsg} failed`);
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Leave application" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }

  if (!row) {
    return (
      <WorkspaceLayout
        header={
          <EnterprisePageHeader
            documentLabel="Leave application"
            primaryActions={
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/leaves')}>
                Back
              </Button>
            }
          />
        }
      >
        <Card>Leave application not found.</Card>
      </WorkspaceLayout>
    );
  }

  const status = String(row.status || '');
  const canEdit = status === 'Draft' || status === 'Pending';
  const canSubmit = status === 'Draft';
  const canApprove = status === 'Pending' || status === 'PendingApproval' || status === 'Draft';
  const canDuty = status === 'Approved';
  const canExtend = status === 'Approved' || status === 'DutyResumed';
  const canCreateSettlement = status === 'Approved' || status === 'DutyResumed';

  const createSettlement = async () => {
    setActionLoading('settlement');
    try {
      const created = await hrmsService.createLeaveSettlement({ leave_application_id: id! });
      message.success('Leave settlement created');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveSettlements() });
      if (created?.id) navigate(`/hr/leave-settlements/${created.id}`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Create leave settlement failed');
    } finally {
      setActionLoading(null);
    }
  };

  const printDoc = () => {
    openHrmsPrintOverlay((onClose) => (
      <PrintableHrmsTransaction
        onClose={onClose}
        data={{
          docType: 'HR_LEAVE_APPLICATION',
          title: 'Leave Application',
          documentNumber: row.leave_application_number,
          employee_name: empLabel(row.employee_id),
          status: row.status,
          meta: [
            { label: 'Leave type', value: String(row.leave_type || '—') },
            {
              label: 'From',
              value: row.from_date ? dayjs(row.from_date).format('DD-MMM-YYYY') : '—',
            },
            {
              label: 'To',
              value: row.to_date ? dayjs(row.to_date).format('DD-MMM-YYYY') : '—',
            },
            {
              label: 'Duty resume',
              value: row.duty_resume_date ? dayjs(row.duty_resume_date).format('DD-MMM-YYYY') : '—',
            },
          ],
          lines: [
            { description: 'Total days', value: String(row.total_days ?? '—') },
            { description: 'Reason', value: String(row.reason || '—') },
          ],
          notes: row.reason,
        }}
      />
    ));
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Leave application"
          documentNumber={row.leave_application_number || '—'}
          statusTag={<Tag color={hrmsStatusColor(status, LEAVE_STATUS_COLOR)}>{status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/leaves')}>
                Back
              </Button>
              <Button icon={<PrinterOutlined />} onClick={printDoc}>
                Print
              </Button>
              {canEdit && (
                <Button icon={<EditOutlined />} onClick={() => navigate(`/hr/leaves/${id}/edit`)}>
                  Edit
                </Button>
              )}
              {canSubmit && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={actionLoading === 'submit'}
                  onClick={() => run('submit', () => hrmsService.submitLeave(id!), 'Submitted')}
                >
                  Submit
                </Button>
              )}
              {canApprove && (
                <>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={actionLoading === 'approve'}
                    onClick={() => run('approve', () => hrmsService.approveLeave(id!), 'Approved')}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    loading={actionLoading === 'reject'}
                    onClick={() =>
                      run('reject', () => hrmsService.rejectLeave(id!, { reason: 'Rejected' }), 'Rejected')
                    }
                  >
                    Reject
                  </Button>
                </>
              )}
              {canCreateSettlement && (
                <Button
                  icon={<DollarOutlined />}
                  loading={actionLoading === 'settlement'}
                  onClick={createSettlement}
                >
                  Create leave settlement
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Details" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Type">{row.leave_type}</Descriptions.Item>
              <Descriptions.Item label="From">
                {row.from_date ? dayjs(row.from_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="To">
                {row.to_date ? dayjs(row.to_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Days">{row.total_days ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Duty resume date">
                {row.duty_resume_date ? dayjs(row.duty_resume_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Duty resumed at">
                {row.duty_resumed_at ? dayjs(row.duty_resumed_at).format('DD-MMM-YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Extension of">
                {row.parent_leave_id ? (
                  <a onClick={() => navigate(`/hr/leaves/${row.parent_leave_id}`)}>
                    Open parent leave
                  </a>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Reason" span={2}>
                {row.reason || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {canDuty && (
            <Card title="Duty resume" size="small" style={{ marginBottom: 16 }}>
              <Form
                form={dutyForm}
                layout="inline"
                initialValues={{ duty_resume_date: dayjs() }}
                onFinish={(v) =>
                  run(
                    'duty',
                    () =>
                      hrmsService.dutyResumeLeave(id!, {
                        duty_resume_date: v.duty_resume_date.format('YYYY-MM-DD'),
                      }),
                    'Duty resumed'
                  )
                }
              >
                <Form.Item name="duty_resume_date" rules={[{ required: true }]}>
                  <DatePicker />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={actionLoading === 'duty'}>
                  Record duty resume
                </Button>
              </Form>
            </Card>
          )}

          {canExtend && (
            <Card title="Extend leave" size="small" style={{ marginBottom: 16 }}>
              <Form
                form={extendForm}
                layout="vertical"
                onFinish={async (v) => {
                  const created = await run(
                    'extend',
                    () =>
                      hrmsService.extendLeave(id!, {
                        to_date: v.to_date.format('YYYY-MM-DD'),
                        reason: v.reason,
                        submit: !!v.submit,
                      }),
                    'Extension created'
                  );
                  if (created && (created as any).id) {
                    navigate(`/hr/leaves/${(created as any).id}`);
                  }
                }}
              >
                <Form.Item name="to_date" label="New end date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: 220 }} />
                </Form.Item>
                <Form.Item name="reason" label="Reason">
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="submit" label="Submit extension for approval" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={actionLoading === 'extend'}>
                  Create extension
                </Button>
              </Form>
            </Card>
          )}

          <EntityAttachmentsCard entityType="HR_LEAVE_APPLICATION" entityId={row.id} title="Attachments" />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_LEAVE_APPLICATION}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.leaveApplication(id!), hrmsKeys.leaveApplications()]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default LeaveApplicationDetailPage;
